document.addEventListener("DOMContentLoaded", () => {
    const categorySelect = document.getElementById("category-select");
    const yearSelect = document.getElementById("year-select");
    const makeSelect = document.getElementById("make-select");
    const modelSelect = document.getElementById("model-select");
    const cabSelect = document.getElementById("cab-select");
    const bedSelect = document.getElementById("bed-select");
    const featuresSelect = document.getElementById("features-select");
    const cabSelectGroup = document.getElementById("cab-select-group");
    const bedSelectGroup = document.getElementById("bed-select-group");
    const boatFeaturesSelect = document.getElementById("boat-features-select");
    const boatFeaturesSelectGroup = document.getElementById("boat-features-select-group");

    /** Internal value for "All Options" — not used in the collection URL (same as make-model/year only) */
    const BOAT_ALL_OPTIONS_VALUE = "__boat_all_options__";

    let csvData = [];
    let isLoading = false;
    const csvCache = new Map();
    const MAX_CACHE_SIZE = 10;

    // Helper function to check if current category is truck-covers
    const isTruckCovers = () => {
        return categorySelect?.value === "truck-covers";
    };

    const isBoatCovers = () => {
        return categorySelect?.value === "boat-covers";
    };

    // Helper function to check if current category is a features collection
    const isFeaturesCollection = () => {
        return featuresSelect !== null;
    };

    const isBoatFeaturesGroupVisible = () => {
        if (!boatFeaturesSelectGroup) return false;
        return boatFeaturesSelectGroup.style.display !== "none";
    };

    // Show/hide boat feature select (Boat Covers only)
    const toggleBoatFeaturesSelect = () => {
        const show = isBoatCovers();
        if (boatFeaturesSelectGroup) {
            boatFeaturesSelectGroup.style.display = show ? "" : "none";
        }
        if (!show && boatFeaturesSelect) {
            boatFeaturesSelect.disabled = true;
            boatFeaturesSelect.innerHTML = '<option value="">SELECT FEATURES</option>';
        }
    };

    // Show/hide cab and bed selects based on category
    const toggleTruckSelects = () => {
        const show = isTruckCovers();
        if (cabSelectGroup) cabSelectGroup.style.display = show ? "" : "none";
        if (bedSelectGroup) bedSelectGroup.style.display = show ? "" : "none";
        
        if (!show) {
            // Reset and disable when hiding
            if (cabSelect) {
                cabSelect.disabled = true;
                populateSelect(cabSelect, [], "SELECT CAB");
            }
            if (bedSelect) {
                bedSelect.disabled = true;
                populateSelect(bedSelect, [], "SELECT BED");
            }
        }
    };

    const cacheCsvData = (key, data) => {
        if (!key || !Array.isArray(data)) return;
        if (csvCache.size >= MAX_CACHE_SIZE) {
            const oldestKey = csvCache.keys().next().value;
            csvCache.delete(oldestKey);
        }
        csvCache.set(key, data);
    };

    const getCachedCsvData = (key) => {
        if (!key) return undefined;
        return csvCache.get(key);
    };

    // Improved CSV parser that handles quoted fields and commas
    const parseCSV = (csvText) => {
        try {
            const lines = csvText.split("\n").filter(line => line.trim());
            if (lines.length === 0) {
                throw new Error("CSV file is empty");
            }

            // Parse header row
            const headers = parseCSVLine(lines[0]);
            if (headers.length === 0) {
                throw new Error("CSV header is empty");
            }

            const data = [];
            
            // Parse data rows
            for (let i = 1; i < lines.length; i++) {
                if (!lines[i].trim()) continue;
                
                try {
                    const values = parseCSVLine(lines[i]);
                    if (values.length !== headers.length) {
                        console.warn(`Row ${i + 1} has ${values.length} columns, expected ${headers.length}. Skipping.`);
                        continue;
                    }
                    
                    const row = {};
                    headers.forEach((header, index) => {
                        row[header.trim()] = (values[index] ? values[index].trim() : "");
                    });
                    data.push(row);
                } catch (error) {
                    console.warn(`Error parsing row ${i + 1}:`, error);
                    continue;
                }
            }

            if (data.length === 0) {
                throw new Error("No valid data rows found in CSV");
            }

            return data;
        } catch (error) {
            console.error("CSV parsing error:", error);
            throw error;
        }
    };

    // Helper function to parse a CSV line, handling quoted fields
    const parseCSVLine = (line) => {
        const values = [];
        let current = "";
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            const nextChar = line[i + 1];
            
            if (char === '"') {
                if (inQuotes && nextChar === '"') {
                    // Escaped quote
                    current += '"';
                    i++; // Skip next quote
                } else {
                    // Toggle quote state
                    inQuotes = !inQuotes;
                }
            } else if (char === ',' && !inQuotes) {
                // End of field
                values.push(current);
                current = "";
            } else {
                current += char;
            }
        }
        
        // Add last field
        values.push(current);
        return values;
    };

    // Function to populate a select element with error handling
    const populateSelect = (selectElement, options, placeholder) => {
        if (!selectElement) {
            console.warn("Select element not found");
            return;
        }

        try {
            selectElement.innerHTML = `<option value="">${placeholder}</option>`;
            
            if (!Array.isArray(options)) {
                console.warn("Options is not an array");
                return;
            }

            options.forEach(option => {
                if (option) {
                    const opt = document.createElement("option");
                    opt.value = option;
                    opt.textContent = option;
                    selectElement.appendChild(opt);
                }
            });

            selectElement.disabled = options.length === 0;
        } catch (error) {
            console.error("Error populating select:", error);
        }
    };

    /**
     * Normalize a value for matching (used when comparing CSV values to URL values)
     * - lowercase
     * - spaces, dots, slashes → hyphens
     * - trim whitespace
     */
    const normalizeValue = (value) => {
        if (!value) return "";
        return value
            .toString()
            .trim()
            .toLowerCase()
            .replace(/[\s.\/]/g, "-")
            .replace(/-+/g, "-")
            .replace(/^-+|-+$/g, "");
    };

    /**
     * Normalize a value for Shopify collection handles (Liquid handleize–style):
     * - lowercase, strip accents
     * - spaces, dots, slashes, underscores → hyphens
     * - only a-z, 0-9, and hyphens in the result (drops *, +, /, quotes, etc.)
     * - repeated hyphens collapsed, trimmed
     */
    const normalizeForShopify = (value) => {
        if (!value) return "";

        let normalized = value.toString().trim().toLowerCase();

        if (typeof normalized.normalize === "function") {
            normalized = normalized
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "");
        }

        normalized = normalized.replace(/[\s._\/]/g, "-");
        normalized = normalized.replace(/[^a-z0-9-]/g, "");
        normalized = normalized.replace(/-+/g, "-");
        normalized = normalized.replace(/^-+|-+$/g, "");

        return normalized;
    };

    /**
     * Find matching option value in select element
     * Used to match URL values to CSV options
     */
    const findOptionValue = (selectElement, tagValue) => {
        if (!selectElement || !tagValue) {
            return "";
        }

        // Decode the tag value
        const decoded = decodeURIComponent(tagValue);

        // For years, do exact match (no normalization)
        const isYearSelect = selectElement.id === "year-select";

        if (isYearSelect) {
            // For years, do exact string match (trimmed)
            const trimmedDecoded = decoded.trim();

            for (const option of selectElement.options) {
                if (option.value && option.value.trim() === trimmedDecoded) {
                    return option.value;
                }
            }
        } else {
            // For make/model, use normalization
            const normalizedTag = normalizeValue(
                decoded.replace(/-/g, " ").replace(/\s+/g, "-")
            );
            for (const option of selectElement.options) {
                if (option.value) {
                    const normalizedOption = normalizeValue(option.value);
                    if (normalizedOption === normalizedTag) {
                        return option.value;
                    }
                }
            }
        }

        return "";
    };

    /**
     * Update makes dropdown based on selected year
     */
    const updateMakes = (year) => {
        if (!csvData || csvData.length === 0) return;
        const makes = [
            ...new Set(
                csvData
                    .filter((row) => {
                        const rowYear = row.YEAR ? row.YEAR.replace("YEAR_", "") : "";
                        return normalizeValue(rowYear) === normalizeValue(year);
                    })
                    .map((row) => row.MAKE ? row.MAKE.replace("MAKE_", "") : "")
            ),
        ]
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
        populateSelect(makeSelect, makes, "SELECT MAKE");
    };

    /**
     * Update models dropdown based on selected year and make
     */
    const updateModels = (year, make) => {
        if (!csvData || csvData.length === 0) return;
        const models = [
            ...new Set(
                csvData
                    .filter((row) => {
                        const rowYear = row.YEAR ? row.YEAR.replace("YEAR_", "") : "";
                        const rowMake = row.MAKE ? row.MAKE.replace("MAKE_", "") : "";
                        return normalizeValue(rowYear) === normalizeValue(year) &&
                               normalizeValue(rowMake) === normalizeValue(make);
                    })
                    .map((row) => row.MODEL ? row.MODEL.replace("MODEL_", "") : "")
            ),
        ]
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
        populateSelect(modelSelect, models, "SELECT MODEL");
    };

    /**
     * Update cabs dropdown based on selected year, make, and model (for trucks only)
     */
    const updateCabs = (year, make, model) => {
        if (!csvData || csvData.length === 0 || !isTruckCovers()) return;
        if (!cabSelect) return;
        
        const cabs = [
            ...new Set(
                csvData
                    .filter((row) => {
                        const rowYear = row.YEAR ? row.YEAR.replace("YEAR_", "") : "";
                        const rowMake = row.MAKE ? row.MAKE.replace("MAKE_", "") : "";
                        const rowModel = row.MODEL ? row.MODEL.replace("MODEL_", "") : "";
                        return normalizeValue(rowYear) === normalizeValue(year) &&
                               normalizeValue(rowMake) === normalizeValue(make) &&
                               normalizeValue(rowModel) === normalizeValue(model);
                    })
                    .map((row) => row.CAB ? row.CAB.replace("CAB_", "") : "")
            ),
        ]
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
        populateSelect(cabSelect, cabs, "SELECT CAB");
    };

    /**
     * Update beds dropdown based on selected year, make, model, and cab (for trucks only)
     */
    const updateBeds = (year, make, model, cab) => {
        if (!csvData || csvData.length === 0 || !isTruckCovers()) return;
        if (!bedSelect) return;
        
        const beds = [
            ...new Set(
                csvData
                    .filter((row) => {
                        const rowYear = row.YEAR ? row.YEAR.replace("YEAR_", "") : "";
                        const rowMake = row.MAKE ? row.MAKE.replace("MAKE_", "") : "";
                        const rowModel = row.MODEL ? row.MODEL.replace("MODEL_", "") : "";
                        const rowCab = row.CAB ? row.CAB.replace("CAB_", "") : "";
                        return normalizeValue(rowYear) === normalizeValue(year) &&
                               normalizeValue(rowMake) === normalizeValue(make) &&
                               normalizeValue(rowModel) === normalizeValue(model) &&
                               normalizeValue(rowCab) === normalizeValue(cab);
                    })
                    .map((row) => row.BED ? row.BED.replace("BED_", "") : "")
            ),
        ]
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
        populateSelect(bedSelect, beds, "SELECT BED");
    };

    const getBoatFeatureFromRow = (row) => {
        if (!row || typeof row !== "object") return "";
        const key = Object.keys(row).find((k) => {
            const n = k.trim().toUpperCase();
            return (
                n === "FEATURES" ||
                n === "FEATURE" ||
                n === "BOAT_FEATURES" ||
                n === "BOAT_FEATURE"
            );
        });
        if (!key) return "";
        let v = String(row[key]).trim();
        if (!v) return "";
        v = v.replace(/^FEATURES_/i, "").replace(/^FEATURE_/i, "");
        return v;
    };

    /**
     * Populate boat features from CSV for Y/M/M. If no feature variants exist, redirect without feature segment.
     */
    const updateBoatFeatures = (year, make, model) => {
        if (!isBoatCovers() || !boatFeaturesSelect || !csvData.length) return;

        const rows = csvData.filter((row) => {
            const rowYear = row.YEAR ? row.YEAR.replace("YEAR_", "") : "";
            const rowMake = row.MAKE ? row.MAKE.replace("MAKE_", "") : "";
            const rowModel = row.MODEL ? row.MODEL.replace("MODEL_", "") : "";
            return (
                normalizeValue(rowYear) === normalizeValue(year) &&
                normalizeValue(rowMake) === normalizeValue(make) &&
                normalizeValue(rowModel) === normalizeValue(model)
            );
        });

        const raw = rows.map(getBoatFeatureFromRow).filter(Boolean);
        const unique = [...new Set(raw)].sort((a, b) => a.localeCompare(b));

        if (unique.length === 0) {
            if (boatFeaturesSelectGroup) boatFeaturesSelectGroup.style.display = "none";
            boatFeaturesSelect.disabled = true;
            boatFeaturesSelect.innerHTML = '<option value="">SELECT FEATURES</option>';
            handleSearch();
            return;
        }

        if (boatFeaturesSelectGroup) boatFeaturesSelectGroup.style.display = "";
        boatFeaturesSelect.innerHTML = "";
        const ph = document.createElement("option");
        ph.value = "";
        ph.textContent = "SELECT FEATURES";
        ph.disabled = true;
        ph.selected = true;
        boatFeaturesSelect.appendChild(ph);

        const allOpt = document.createElement("option");
        allOpt.value = BOAT_ALL_OPTIONS_VALUE;
        allOpt.textContent = "All Options";
        boatFeaturesSelect.appendChild(allOpt);

        unique.forEach((f) => {
            const opt = document.createElement("option");
            opt.value = f;
            opt.textContent = f;
            boatFeaturesSelect.appendChild(opt);
        });

        boatFeaturesSelect.disabled = false;
    };

    // Function to handle search submission with new URL format
    const handleSearch = () => {
        const selectedYear = yearSelect?.value;
        const selectedMake = makeSelect?.value;
        const selectedModel = modelSelect?.value;
        const selectedCab = isTruckCovers() ? (cabSelect ? cabSelect.value : "") : "";
        const selectedBed = isTruckCovers() ? (bedSelect ? bedSelect.value : "") : "";
        const selectedFeatures = isFeaturesCollection() ? (featuresSelect ? featuresSelect.value : "") : "";

        // Validate required selections (Year, Make, Model)
        if (!selectedYear || !selectedMake || !selectedModel) {
            console.warn("Year, Make, and Model must be selected before searching");
            return;
        }

        // For truck-covers, require cab and bed
        if (isTruckCovers() && (!selectedCab || !selectedBed)) {
            console.warn("Cab and Bed must be selected for truck covers");
            return;
        }

        // Boat: when feature UI is shown, pick a CSV feature or "All Options" (placeholder alone is invalid)
        if (isBoatCovers() && isBoatFeaturesGroupVisible()) {
            const bf = boatFeaturesSelect?.value ?? "";
            if (bf === "") {
                console.warn("Select a boat feature or All Options");
                return;
            }
        }

        // For features collections, require features
        if (isFeaturesCollection() && !selectedFeatures) {
            console.warn("Features must be selected for features collections");
            return;
        }

        try {
            // Normalize make and model for Shopify collection handle
            const normalizedMake = normalizeForShopify(selectedMake);
            const normalizedModel = normalizeForShopify(selectedModel);
            
            // Build URL based on collection type
            let collectionHandle = `${normalizedMake}-${normalizedModel}`;

            if (isBoatCovers()) {
                const bf = boatFeaturesSelect?.value ?? "";
                if (bf && bf !== BOAT_ALL_OPTIONS_VALUE) {
                    const normalizedBoatFeature = normalizeForShopify(bf);
                    if (!normalizedBoatFeature) {
                        console.warn("Boat feature normalized to an empty handle segment");
                        alert("Could not build a valid search URL for this selection. Please try another option.");
                        return;
                    }
                    collectionHandle = `${normalizedMake}-${normalizedModel}-${normalizedBoatFeature}`;
                }
            }
            
            if (isTruckCovers() && selectedCab && selectedBed) {
                // Format: /collections/make-model-cab-bed/year
                const normalizedCab = normalizeForShopify(selectedCab);
                const normalizedBed = normalizeForShopify(selectedBed);
                if (!normalizedCab || !normalizedBed) {
                    console.warn("Cab or bed normalized to an empty handle segment");
                    alert("Could not build a valid search URL for this selection. Please try another option.");
                    return;
                }
                collectionHandle = `${normalizedMake}-${normalizedModel}-${normalizedCab}-${normalizedBed}`;
            } else if (isFeaturesCollection() && selectedFeatures) {
                // Format: /collections/make-model-features/year
                const normalizedFeatures = normalizeForShopify(selectedFeatures);
                if (!normalizedFeatures) {
                    console.warn("Features normalized to an empty handle segment");
                    alert("Could not build a valid search URL for this selection. Please try another option.");
                    return;
                }
                collectionHandle = `${normalizedMake}-${normalizedModel}-${normalizedFeatures}`;
            }
            // Otherwise: /collections/make-model/year (default format)

            if (!normalizedMake || !normalizedModel || !collectionHandle.replace(/-/g, "")) {
                console.warn("Search could not build a valid collection handle from the selection");
                alert("Could not build a valid search URL for this selection. Please try another option.");
                return;
            }
            
            const url = `/collections/${collectionHandle}/${selectedYear}`;
            
            const collectionType = isTruckCovers()
                ? "truck-covers"
                : isBoatCovers()
                    ? "boat-covers"
                    : isFeaturesCollection()
                        ? "features"
                        : "standard";
            console.log("Collection type:", collectionType);
            console.log("Redirecting to URL:", url);
            
            // Redirect the user to the new URL
            window.location.href = url;
        } catch (error) {
            console.error("Error handling search:", error);
            alert("An error occurred while processing your search. Please try again.");
        }
    };

    // Function to show loading state
    const setLoadingState = (isLoading) => {
        if (yearSelect) yearSelect.disabled = isLoading || !categorySelect?.value;
        if (makeSelect) makeSelect.disabled = isLoading || !yearSelect?.value;
        if (modelSelect) modelSelect.disabled = isLoading || !makeSelect?.value;
        if (isTruckCovers()) {
            if (cabSelect) cabSelect.disabled = isLoading || !modelSelect?.value;
            if (bedSelect) bedSelect.disabled = isLoading || !cabSelect?.value;
        }
        if (isBoatCovers() && boatFeaturesSelect) {
            const groupHidden = boatFeaturesSelectGroup?.style.display === "none";
            boatFeaturesSelect.disabled =
                isLoading || !modelSelect?.value || groupHidden;
        }
        if (isFeaturesCollection()) {
            if (featuresSelect) featuresSelect.disabled = isLoading || !modelSelect?.value;
        }
    };

    // Event listener for Category selection with improved error handling
    categorySelect?.addEventListener("change", async () => {
        const selectedCategory = categorySelect.value;
        
        // Toggle truck-specific selects
        toggleTruckSelects();
        toggleBoatFeaturesSelect();

        // Reset dependent selects
        if (yearSelect) {
            yearSelect.disabled = true;
            populateSelect(yearSelect, [], "SELECT YEAR");
        }
        if (makeSelect) {
            makeSelect.disabled = true;
            populateSelect(makeSelect, [], "SELECT MAKE");
        }
        if (modelSelect) {
            modelSelect.disabled = true;
            populateSelect(modelSelect, [], "SELECT MODEL");
        }
        if (boatFeaturesSelect && isBoatCovers()) {
            boatFeaturesSelect.disabled = true;
            boatFeaturesSelect.innerHTML = '<option value="">SELECT FEATURES</option>';
        }

        if (!selectedCategory) {
            return;
        }

        const cacheKey = selectedCategory;
        const cachedData = getCachedCsvData(cacheKey);

        if (cachedData && Array.isArray(cachedData) && cachedData.length > 0) {
            csvData = cachedData;

            const uniqueYears = [...new Set(
                csvData
                    .map(row => row.YEAR ? row.YEAR.replace("YEAR_", "") : "")
                    .filter(year => year)
            )].sort((a, b) => {
                const numA = parseInt(a, 10);
                const numB = parseInt(b, 10);
                return Number.isNaN(numA) || Number.isNaN(numB) ? a.localeCompare(b) : numB - numA;
            });

            populateSelect(yearSelect, uniqueYears, "SELECT YEAR");
            return;
        }

        const csvFile = categorySelect.options[categorySelect.selectedIndex]?.dataset?.csv;
        
        if (!csvFile) {
            console.error("No CSV file specified for category:", selectedCategory);
            return;
        }

        // Prevent multiple simultaneous loads
        if (isLoading) {
            console.warn("CSV is already loading");
            return;
        }

        isLoading = true;
        setLoadingState(true);

        try {
            // Add protocol if missing (for CDN URLs)
            const csvUrl = csvFile.startsWith('//') ? `https:${csvFile}` : csvFile;
            
            const response = await fetch(csvUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'text/csv'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const text = await response.text();
            
            if (!text || text.trim().length === 0) {
                throw new Error("CSV file is empty");
            }

            csvData = parseCSV(text);

            if (!csvData || csvData.length === 0) {
                throw new Error("No data found in CSV file");
            }

            cacheCsvData(cacheKey, csvData);

            // Extract unique years
            const uniqueYears = [...new Set(
                csvData
                    .map(row => row.YEAR ? row.YEAR.replace("YEAR_", "") : "")
                    .filter(year => year)
            )].sort((a, b) => {
                const numA = parseInt(a);
                const numB = parseInt(b);
                return isNaN(numA) || isNaN(numB) ? a.localeCompare(b) : numB - numA;
            });

            populateSelect(yearSelect, uniqueYears, "SELECT YEAR");
            
        } catch (error) {
            console.error("Error loading CSV:", error);
            alert(`Failed to load data for ${selectedCategory}. Please try again later.`);
            csvData = [];
        } finally {
            isLoading = false;
            setLoadingState(false);
        }
    });

    // Event listener for Year selection with error handling
    yearSelect?.addEventListener("change", () => {
        const selectedYear = yearSelect.value;
        
        if (makeSelect) {
            makeSelect.disabled = true;
            populateSelect(makeSelect, [], "SELECT MAKE");
        }
        if (modelSelect) {
            modelSelect.disabled = true;
            populateSelect(modelSelect, [], "SELECT MODEL");
        }
        if (boatFeaturesSelect && isBoatCovers()) {
            boatFeaturesSelect.disabled = true;
            boatFeaturesSelect.innerHTML = '<option value="">SELECT FEATURES</option>';
        }

        if (!selectedYear || !csvData || csvData.length === 0) {
            return;
        }

        try {
            const filteredData = csvData.filter(row => 
                (row.YEAR ? row.YEAR.replace("YEAR_", "") : "") === selectedYear
            );

            if (filteredData.length === 0) {
                console.warn("No makes found for year:", selectedYear);
                return;
            }

            const uniqueMakes = [...new Set(
                filteredData
                    .map(row => row.MAKE ? row.MAKE.replace("MAKE_", "") : "")
                    .filter(make => make)
            )].sort();

            populateSelect(makeSelect, uniqueMakes, "SELECT MAKE");
        } catch (error) {
            console.error("Error filtering by year:", error);
        }
    });

    // Event listener for Make selection with error handling
    makeSelect?.addEventListener("change", () => {
        const selectedYear = yearSelect?.value;
        const selectedMake = makeSelect?.value;
        
        if (modelSelect) {
            modelSelect.disabled = true;
            populateSelect(modelSelect, [], "SELECT MODEL");
        }
        if (cabSelect && isTruckCovers()) {
            cabSelect.disabled = true;
            populateSelect(cabSelect, [], "SELECT CAB");
        }
        if (bedSelect && isTruckCovers()) {
            bedSelect.disabled = true;
            populateSelect(bedSelect, [], "SELECT BED");
        }
        if (boatFeaturesSelect && isBoatCovers()) {
            boatFeaturesSelect.disabled = true;
            boatFeaturesSelect.innerHTML = '<option value="">SELECT FEATURES</option>';
        }

        if (!selectedMake || !selectedYear || !csvData || csvData.length === 0) {
            return;
        }

        try {
            const filteredData = csvData.filter(row => 
                (row.YEAR ? row.YEAR.replace("YEAR_", "") : "") === selectedYear &&
                (row.MAKE ? row.MAKE.replace("MAKE_", "") : "") === selectedMake
            );

            if (filteredData.length === 0) {
                console.warn("No models found for year and make:", selectedYear, selectedMake);
                return;
            }

            const uniqueModels = [...new Set(
                filteredData
                    .map(row => row.MODEL ? row.MODEL.replace("MODEL_", "") : "")
                    .filter(model => model)
            )].sort();

            populateSelect(modelSelect, uniqueModels, "SELECT MODEL");
        } catch (error) {
            console.error("Error filtering by make:", error);
        }
    });

    // Event listener for Model selection
    modelSelect?.addEventListener("change", () => {
        const selectedYear = yearSelect?.value;
        const selectedMake = makeSelect?.value;
        const selectedModel = modelSelect?.value;

        // For trucks, update cabs dropdown
        if (isTruckCovers() && selectedYear && selectedMake && selectedModel) {
            updateCabs(selectedYear, selectedMake, selectedModel);
        }

        // Boat: load feature options (or redirect if no feature variants for this Y/M/M)
        if (isBoatCovers() && selectedYear && selectedMake && selectedModel) {
            updateBoatFeatures(selectedYear, selectedMake, selectedModel);
            return;
        }

        // For standard collections (not truck, not features, not boat), trigger search immediately
        if (!isTruckCovers() && !isFeaturesCollection() && !isBoatCovers() && selectedYear && selectedMake && selectedModel) {
            handleSearch();
        }
        // For truck and features collections, wait for additional selections
    });

    // Event listener for Cab selection (trucks only)
    cabSelect?.addEventListener("change", () => {
        const selectedYear = yearSelect?.value;
        const selectedMake = makeSelect?.value;
        const selectedModel = modelSelect?.value;
        const selectedCab = cabSelect?.value;

        if (isTruckCovers() && selectedYear && selectedMake && selectedModel && selectedCab) {
            updateBeds(selectedYear, selectedMake, selectedModel, selectedCab);
        }
    });

    // Event listener for Bed selection - triggers search for trucks
    bedSelect?.addEventListener("change", () => {
        if (isTruckCovers()) {
            const selectedYear = yearSelect?.value;
            const selectedMake = makeSelect?.value;
            const selectedModel = modelSelect?.value;
            const selectedCab = cabSelect?.value;
            const selectedBed = bedSelect?.value;

            if (selectedYear && selectedMake && selectedModel && selectedCab && selectedBed) {
                handleSearch();
            }
        }
    });

    // Event listener for Features selection - triggers search for features collections
    featuresSelect?.addEventListener("change", () => {
        if (isFeaturesCollection()) {
            const selectedYear = yearSelect?.value;
            const selectedMake = makeSelect?.value;
            const selectedModel = modelSelect?.value;
            const selectedFeatures = featuresSelect?.value;

            if (selectedYear && selectedMake && selectedModel && selectedFeatures) {
                handleSearch();
            }
        }
    });

    // Boat feature selection — CSV feature or "All Options" → redirect
    boatFeaturesSelect?.addEventListener("change", () => {
        if (!isBoatCovers()) return;
        const selectedYear = yearSelect?.value;
        const selectedMake = makeSelect?.value;
        const selectedModel = modelSelect?.value;
        const v = boatFeaturesSelect?.value ?? "";
        if (!selectedYear || !selectedMake || !selectedModel || v === "") {
            return;
        }
        handleSearch();
    });

    // Initialize truck / boat auxiliary selects visibility on page load
    toggleTruckSelects();
    toggleBoatFeaturesSelect();

    // When category is pre-selected (e.g. smart-search.liquid on a page), load CSV and populate years
    const initialCategory = categorySelect?.value;
    if (initialCategory) {
        const cachedData = getCachedCsvData(initialCategory);
        if (cachedData && Array.isArray(cachedData) && cachedData.length > 0) {
            csvData = cachedData;
            const uniqueYears = [...new Set(
                csvData
                    .map(row => row.YEAR ? row.YEAR.replace("YEAR_", "") : "")
                    .filter(year => year)
            )].sort((a, b) => {
                const numA = parseInt(a);
                const numB = parseInt(b);
                return isNaN(numA) || isNaN(numB) ? a.localeCompare(b) : numB - numA;
            });
            populateSelect(yearSelect, uniqueYears, "SELECT YEAR");
            if (yearSelect) yearSelect.disabled = false;
            setLoadingState(false);
        } else {
            // No cache: trigger category change to fetch CSV and populate year dropdown
            categorySelect.dispatchEvent(new Event("change"));
        }
    }
});


